import React from 'react';
import PropTypes from 'prop-types';
import {
    ListItemButton, ListItemText, ListItemSecondaryAction, List
} from '@mui/material';
import { List as IList } from 'immutable';
import ListItem from '@mui/material/ListItem';
import CheckIcon from '@mui/icons-material/Check';
import { connect } from 'react-redux';
import SiteSwitchDialog from './SiteSwitchDialog';
import { allowedLocaleList } from './CurrencyLocaleUtils';
import { getIntlCurrencyCode } from '../intl/intlSelectors';
import { trackCurrencyLanguageChange } from '../segment/segmentModule';
import { isLocaleAllowed } from '../utils/landingPageUtils';
import Flag from './Flag';

const LanguageName = ({ languageCode }) => new Intl.DisplayNames(languageCode, { type: 'language' }).of(languageCode);

LanguageName.propTypes = {
    languageCode: PropTypes.string.isRequired
};

const CountryName = ({ countryCode, languageCode }) => new Intl.DisplayNames(
    [languageCode], { type: 'region' }
).of(countryCode);

CountryName.propTypes = {
    countryCode: PropTypes.string.isRequired
};


const LanguagePicker = ({
    selectedLocale, localesForSelectedCountry, popupStatus, setLocale, selectedCurrency, trackCurrencyLanguageAction, location
}) => {
    const [isDialogOpen, setDialogOpen] = React.useState(false);
    const [newSelectedLanguageName, setNewSelectedLanguageName] = React.useState(selectedLocale);
    const [newselectedLocale, setNewselectedLocale] = React.useState(selectedLocale);

    
    const newLocalesForSelectedCountry = React.useMemo(() => {
        
        const regionDisplayNamesCache = new Map();
        const languageDisplayNamesCache = new Map();

        const getRegionName = (lang, country) => {
            if (!regionDisplayNamesCache.has(lang)) {
                regionDisplayNamesCache.set(
                    lang,
                    new Intl.DisplayNames([lang], { type: 'region' })
                );
            }
            return regionDisplayNamesCache.get(lang).of(country.toUpperCase());
        };

        const getLanguageName = (lang) => {
            if (!languageDisplayNamesCache.has(lang)) {
                languageDisplayNamesCache.set(
                    lang,
                    new Intl.DisplayNames([lang], { type: 'language' })
                );
            }
            return languageDisplayNamesCache.get(lang).of(lang);
        };

        const otherLocales = localesForSelectedCountry
            .filter(locale => locale !== selectedLocale)
            .sort((localeA, localeB) => {
                const [langA, countryA] = localeA.split('-');
                const [langB, countryB] = localeB.split('-');

                const countryNameA = getRegionName(langA, countryA);
                const countryNameB = getRegionName(langB, countryB);

                const countryCompare = countryNameA.localeCompare(countryNameB, 'en');

                if (countryCompare !== 0) {
                    return countryCompare;
                }

                const languageNameA = getLanguageName(langA);
                const languageNameB = getLanguageName(langB);

                return languageNameA.localeCompare(languageNameB, 'en');
            });

        return IList([selectedLocale, ...otherLocales]);
    }, [selectedLocale, localesForSelectedCountry]);


    const handleCloseModel = (value) => {
        setDialogOpen(value);
    };
    const handleLanguageChange = (supportedLocale, languageCode) => {
        const isAllowed = isLocaleAllowed(supportedLocale, allowedLocaleList);

        if (!isAllowed) {
            setDialogOpen(true);
            setNewSelectedLanguageName(LanguageName({ languageCode }));
            setNewselectedLocale(supportedLocale);
        } else if (selectedLocale !== supportedLocale) {
            setLocale(supportedLocale);
            popupStatus(false);
            trackCurrencyLanguageAction({
                currentCurrencyCode: selectedCurrency, nextCurrencyCode: selectedCurrency, currentLanguage: selectedLocale, nextLanguage: supportedLocale, location
            });
        }
    };
    return (
        <>
            {}
            {}
            {isDialogOpen && <SiteSwitchDialog supportedLocale={newselectedLocale} language={newSelectedLanguageName} isOpen={isDialogOpen} handleCloseModel={handleCloseModel} />}
            <List className='picker-list'>
                {newLocalesForSelectedCountry.map((supportedLocale) => {
                    const [languageCode, countryCode] = supportedLocale.split('-');
                    return (
                        <ListItem className='list-item' key={supportedLocale} disablePadding>
                            <ListItemButton className='list-item-button' onClick={() => handleLanguageChange(supportedLocale, languageCode)}>
                                <Flag countryCode={countryCode} />
                                <ListItemText
                                    className='list-item-text list-item-currency-text'
                                    primary={<LanguageName languageCode={languageCode} />}
                                    secondary={(
                                        <CountryName
                                            countryCode={countryCode.toUpperCase()}
                                            languageCode={languageCode}
                                        />
                                    )}
                                />
                                {supportedLocale === selectedLocale && (
                                    <ListItemSecondaryAction className='list-item-selected'>
                                        <CheckIcon className='check-icon' />
                                    </ListItemSecondaryAction>
                                )}
                            </ListItemButton>
                            <hr />
                        </ListItem>
                    );
                })}
            </List>
        </>
    );
};

LanguagePicker.defaultProps = {
    popupStatus: () => {}
};

LanguagePicker.propTypes = {
    selectedLocale: PropTypes.string.isRequired,
    localesForSelectedCountry: PropTypes.instanceOf(IList).isRequired,
    popupStatus: PropTypes.func,
    setLocale: PropTypes.func.isRequired,
    selectedCurrency: PropTypes.func.isRequired,
    trackCurrencyLanguageAction: PropTypes.func.isRequired,
    location: PropTypes.string.isRequired
};

const mapStateToProps = state => ({
    selectedCurrency: getIntlCurrencyCode(state)
});

const mapDispatchToProps = {
    trackCurrencyLanguageAction: trackCurrencyLanguageChange
};

export default connect(mapStateToProps, mapDispatchToProps)(LanguagePicker);
